// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

      // 1. ✅ STOP if no token exists in storage.
      // This prevents the browser from making an unauthenticated GET /api/auth/me request and throwing a 401 error.
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authApi.getMe();
        const userData = response.data?.user || response.data?.data?.user || response.data;
        setUser(userData);
      } catch (error) {
        console.log('Session expired or invalid token');
        // Clear invalid token from storage
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authApi.login(email, password);
      
      // 🔍 Debug log: Check your browser console when logging in to see what your backend returns!
      console.log('🔑 Backend Login Response:', response.data);

      const resData = response.data?.data || response.data;
      const user = resData?.user || resData;
      
      // Look for the token across common key names
      const token = resData?.token || resData?.accessToken || response.data?.token || response.data?.accessToken;

      if (!token) {
        console.warn('⚠️ WARNING: No token was found in the login response! Check response structure above.');
      }

      setUser(user);

      if (token) {
        const targetStorage = rememberMe ? localStorage : sessionStorage;
        const otherStorage = rememberMe ? sessionStorage : localStorage;

        // Clean opposite storage
        otherStorage.removeItem('auth_token');
        otherStorage.removeItem('user');

        targetStorage.setItem('auth_token', token);
        targetStorage.setItem('user', JSON.stringify(user));
      }

      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user');
      
      setUser(null);
      window.location.href = '/login';
    }
  };

  const updateUser = (data) => {
    const updatedUser = { ...user, ...data };
    setUser(updatedUser);
    
    if (localStorage.getItem('user')) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } else if (sessionStorage.getItem('user')) {
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isVendor: user?.role === 'vendor',
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};